import { useContext, useState } from "react"

import { 
  Divider, 
  EmptyPlaceholder, 
  RefreshButton 
} from "@components"

import { NotificationContext } from "@context"

import { 
  UPDATE_SB_ACCOUNT_LINK_MUTATION,
} from "@graphql"

import { getGraphQLErrorMessage } from "@helpers"
import { useMutation } from "@apollo/client"

import { 
  Autocomplete, 
  AutocompleteItem, 
  Button, 
  Chip,
  Link,
} from "@heroui/react"

import PropTypes from "prop-types"

const SBAccountSelector = ({ 
  client, 
  handleClose,
  handleRefreshSBAccounts,
  handleUpdateSBAccounts,
  isRefreshingSBAccounts,
  sbAccounts,
  sbAccountEmail, 
  staffMember 
}) => {
  const { notify } = useContext(NotificationContext)
  const [selectedEmail, setSelectedEmail] = useState(sbAccountEmail) 
  
  const [updateSbAccountLink, { loading: isSaving }] = useMutation(UPDATE_SB_ACCOUNT_LINK_MUTATION, {
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while updating SB account link."), open: true }),
    onCompleted: () => {
      handleClose()
      notify({ title: "Success", type: "success", content: "SB account link updated successfully.", open: true })
    }
  })
  
  const handleSave = () => {
    updateSbAccountLink({
      variables: {
        input: {
          client_id: client.id,
          email: selectedEmail,
          user_id: staffMember.user_id
        }
      },

      update: (cache) => {
        const setAvailability = (id, value) => {
          cache.modify({
            id: cache.identify({ __typename: "SBAccount", id }),
            fields: {
              is_available() {
                return value
              }
            }
          })
        }

        // Mark the old account as available
        if (sbAccountEmail !== null) 
        {
          const previousSbAccount = sbAccounts.find(account => account.email === sbAccountEmail)

          if (previousSbAccount) {
            setAvailability(previousSbAccount.id, true)
          }
        }

        // Mark the new account as unavailable
        if (selectedEmail !== null)
        {
          const currentSbAccount = sbAccounts.find(account => account.email === selectedEmail)
          
          if (currentSbAccount) {
            setAvailability(currentSbAccount.id, false)
          }
        }

        // Update local sbAccounts state
        const updatedAccounts = sbAccounts.map(account => {
          if (account.email === sbAccountEmail) return { ...account, is_available: true }
          if (account.email === selectedEmail) return { ...account, is_available: false }
          return account
        })

        handleUpdateSBAccounts(updatedAccounts)
      }
    })
  }
    
  const allSBAccountEmails = sbAccounts?.map(account => account.email) || []
  const availableSBAccountEmails = sbAccounts?.filter(account => account.is_available).map(account => account.email) || []  
  const validCurrentSelections = [sbAccountEmail, selectedEmail].filter(email => allSBAccountEmails.includes(email))

  const emails = [...new Set([...availableSBAccountEmails, ...validCurrentSelections])]
    .filter(email => email !== null && email !== undefined)  
    .sort()

  const isDirty = selectedEmail !== sbAccountEmail

  return (
    <div>
      {availableSBAccountEmails.length === 0 && sbAccountEmail === null && (
        <EmptyPlaceholder 
          classNames="mt-2" 
          title="No Available Accounts" 
          description="You have no available accounts to link to. Either all accounts have already been assigned or accounts haven't been created yet. If using SSO an account will automatically be created when the user logs into SchoolBooking." 
        />
      )}

      {emails.length > 0 && (
        <div>
          <p className="mb-6">
            Non-admin staff must be linked to a SchoolBooking account to access the parents system.
          </p>

          <p className="mb-4">
            If an account does not exist, you can <Link href={`${process.env.REACT_APP_SB_URL}/apps/administration/users/index.php`} isExternal>create one here</Link>. 
          </p>
          
          <p className="mb-4">
            <span className="font-semibold text-red-600">Note:</span> SSO accounts are generated automatically upon the user's first login, but they may still need to be manually linked here if the system cannot automatically match to a MIS email.
          </p>

          <Divider className="my-6" />

          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold">Link Account</h2> 
              <Chip className="font-semibold text-white" color="success">{emails.length} Available</Chip>
            </div>

            <p className="mb-2 text-base text-content2-foreground">
              Link a SB account to link to this staff member. To unlink an account, simply clear the selection and save.
            </p>
            
            <div className="flex items-center gap-1">
              <Autocomplete menuTrigger="input" onSelectionChange={setSelectedEmail} autoFocus={false} selectedKey={selectedEmail} isLoading={isRefreshingSBAccounts}>
                {emails.map((email) => (
                  <AutocompleteItem key={email}>
                    {email}
                  </AutocompleteItem>
                ))}
              </Autocomplete>
              
              <RefreshButton handleClick={handleRefreshSBAccounts} isLoading={isRefreshingSBAccounts} />
            </div> 

            <p className="mt-1 text-xs text-red-600">
              * If you've created or edited an account recently and it doesn't appear in the list then click the refresh button.
            </p>
          </div>
        </div>
      )}

      <Button color="secondary" className="mt-4 mb-2 font-semibold" onPress={handleSave} isLoading={isSaving} isDisabled={!isDirty} fullWidth>Save</Button>
      <Button onPress={handleClose} fullWidth>Cancel</Button>
    </div>
  )
}

SBAccountSelector.propTypes = {
  client: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleRefreshSBAccounts: PropTypes.func.isRequired,
  handleUpdateSBAccounts: PropTypes.func.isRequired,
  isRefreshingSBAccounts: PropTypes.bool.isRequired,
  sbAccounts: PropTypes.array.isRequired,
  sbAccountEmail: PropTypes.string,
  staffMember: PropTypes.object.isRequired,
}

export default SBAccountSelector